import { skipToken } from '@reduxjs/toolkit/query'
import { IFormThingsToImprove } from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Static } from 'core-components/types'
import { Crud } from 'core/types'
import { KeyInitiative, useGetKeyInitiativesQuery } from 'partner360/entities/keyInitiatives'

import { mapActivitiesWithBusinessPriorities } from '../../helpers/activityBusinessPriorities'
import { useMdfActivitiesRefresh } from '../../hooks/useMdfActivitiesRefresh'
import { requestMdfActivityApply, selectMdfActivities } from '../../model'
import { IMdfActivity } from '../../model/types'
import { MdfActivityDetailsModal } from '../mdfActivityDetailsModal'

const EMPTY_INITIATIVES: KeyInitiative[] = []

export interface IMdfActivityDetailsEditorProps {
  activityId: number | null
  partnerId: string
  fYear: number
  programType: ProgramTypes
  onClose: () => void
  onDataChanged?: () => void
}

/**
 * Loads the MDF dashboard's complete activity context and opens its own details editor.
 * Consumers outside the dashboard only provide the selected activity identity and context;
 * editing, linking and initiative creation stay owned by the MDF feature.
 */
export const MdfActivityDetailsEditor = ({
  activityId,
  partnerId,
  fYear,
  programType,
  onClose,
  onDataChanged,
}: IMdfActivityDetailsEditorProps) => {
  const dispatch = useAppDispatch()
  const { kiBusinessPriorities } = useSelector(selectJBPSettings)
  const activities = useSelector(selectMdfActivities)
  const shouldLoad = activityId !== null && Boolean(partnerId) && Boolean(fYear)
  const { isLoadedForCurrentContext } = useMdfActivitiesRefresh(partnerId, {
    fYear,
    programType,
    skip: !shouldLoad,
  })
  const { data: initiatives = EMPTY_INITIATIVES, refetch: refetchInitiatives } =
    useGetKeyInitiativesQuery(shouldLoad ? { partnerId, fYear, programType } : skipToken)
  const [createForActivity, setCreateForActivity] = useState<IMdfActivity | null>(null)
  const [createName, setCreateName] = useState('')

  const activitiesWithBusinessPriorities = useMemo(
    () =>
      isLoadedForCurrentContext
        ? mapActivitiesWithBusinessPriorities(activities, initiatives, kiBusinessPriorities)
        : [],
    [activities, initiatives, isLoadedForCurrentContext, kiBusinessPriorities]
  )
  const selectedActivity = useMemo(() => {
    const exactMatch = activitiesWithBusinessPriorities.find(
      (activity) => activity.activityId === activityId
    )

    if (exactMatch || activityId === null) return exactMatch ?? null

    // Simulated cockpit responses can expose the same MDF ActivityID with a negative sign.
    return (
      activitiesWithBusinessPriorities.find(
        (activity) => Math.abs(activity.activityId) === Math.abs(activityId)
      ) ?? null
    )
  }, [activitiesWithBusinessPriorities, activityId])
  const initiativesSource = useMemo(
    () =>
      new Crud<KeyInitiative>({
        fetcher: new Static(initiatives),
        keyField: 'EntryId',
        defaultPaging: {
          page: 0,
          pageSize: initiatives.length,
        },
      }),
    [initiatives]
  )
  const initiativesById = useMemo(
    () =>
      new Map(
        initiatives.map((initiative) => [
          initiative.EntryId,
          initiative.NameExt ?? initiative.Name ?? `Unnamed #${initiative.EntryId}`,
        ])
      ),
    [initiatives]
  )
  const initiativeById = useMemo(
    () => new Map(initiatives.map((initiative) => [initiative.EntryId, initiative])),
    [initiatives]
  )
  const selectedInitiative =
    selectedActivity?.keyInitiativeId != null
      ? (initiativeById.get(selectedActivity.keyInitiativeId) ?? null)
      : null

  const handleLink = useCallback(
    async (activity: IMdfActivity, keyInitiativeId: number | null) => {
      try {
        await dispatch(requestMdfActivityApply({ activity, fYear, keyInitiativeId })).unwrap()
        onDataChanged?.()
      } catch {
        // The MDF state exposes the save error and the editor remains open for a retry.
      }
    },
    [dispatch, fYear, onDataChanged]
  )

  const handleCreateInitiative = useCallback((activity: IMdfActivity, typedValue: string) => {
    setCreateForActivity(activity)
    setCreateName(typedValue)
  }, [])

  const closeCreateDialog = useCallback(() => {
    setCreateForActivity(null)
    setCreateName('')
  }, [])

  const handleCreateFinished = useCallback(
    async (newData: IFormThingsToImprove[]) => {
      const savedEntryId = newData[0]?.EntryId

      if (createForActivity && savedEntryId) {
        await dispatch(
          requestMdfActivityApply({
            activity: createForActivity,
            fYear,
            keyInitiativeId: savedEntryId,
          })
        ).unwrap()
        onDataChanged?.()
      }

      refetchInitiatives()
      closeCreateDialog()
    },
    [closeCreateDialog, createForActivity, dispatch, fYear, onDataChanged, refetchInitiatives]
  )

  return (
    <>
      <MdfActivityDetailsModal
        show={selectedActivity !== null}
        activity={selectedActivity}
        initiative={selectedInitiative}
        initiativesSource={initiativesSource}
        initiativesById={initiativesById}
        fYear={fYear}
        onLink={handleLink}
        onCreateInitiative={handleCreateInitiative}
        onSaved={onDataChanged}
        onClose={onClose}
      />
      <KeyInitiativeEditor
        initialState={createName ? { Name: createName } : undefined}
        isDialogOpened={createForActivity !== null}
        isDataInsightPresent={programType !== ProgramTypes.Retail}
        onEditCancel={closeCreateDialog}
        onUpdateFinished={handleCreateFinished}
      />
    </>
  )
}

MdfActivityDetailsEditor.displayName = 'MdfActivityDetailsEditor'
